import React, { useEffect, useRef } from "react";
import { pickColor, rgba, hexToRgb } from "./internal/colorUtils";

export const key = "martingale-walks";

const DEFAULT_BG = "#0a0f1c";
const DEFAULT_RECENT = "#22d3ee";   // currently-being-drawn path color
const DEFAULT_OLD = "#a78bfa";      // oldest still-visible historic path
const DEFAULT_AXIS = "#475569";     // E[X_t] = 0 reference axis

// We render a sequence of independent symmetric random walks all
// starting from a common origin on the left edge — the canonical
// discrete martingale X_{t+1} = X_t + ε with ε ~ N(0, σ²) and so
// E[X_{t+1} | X_t] = X_t.
//
// Pacing requested by the user: paths are drawn one at a time. The
// active path traces from x=0 to x=width over PATH_DURATION_MS, and
// only when it reaches the right edge does the next path start. As
// each path completes, it joins a small history buffer and fades with
// age, so the canvas slowly accumulates a record of recent realizations
// that thins out behind the bright leading edge.
const PATH_DURATION_MS = 4200;
const MAX_HISTORY = 16;
const STEPS_PER_PATH = 240;     // smoothness — also drives σ_endpoint = √N
const STEP_SIGMA = 1.0;         // per-step standard deviation (raw units)
const AMPLITUDE_FRAC = 0.30;    // max ±vertical excursion as fraction of height

// Box-Muller transform → ~N(0, 1). Cheap and accurate enough for a
// background visual; we don't need cryptographic-quality randomness.
function gauss() {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function blendRgba(hexA, hexB, t, alpha) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return rgba(hexA, alpha);
  const clamp = Math.max(0, Math.min(1, t));
  const r = Math.round(a.r + (b.r - a.r) * clamp);
  const g = Math.round(a.g + (b.g - a.g) * clamp);
  const bl = Math.round(a.b + (b.b - a.b) * clamp);
  return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
}

function generatePath() {
  const points = new Array(STEPS_PER_PATH);
  points[0] = 0;
  for (let i = 1; i < STEPS_PER_PATH; i += 1) {
    points[i] = points[i - 1] + gauss() * STEP_SIGMA;
  }
  return points;
}

export default function MartingaleWalks({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([
    DEFAULT_BG,
    DEFAULT_RECENT,
    DEFAULT_OLD,
    DEFAULT_AXIS,
  ]);
  // active: { points, startTs }  — the path currently being traced L→R
  // history: completed paths, newest first; alpha decays with index.
  const stateRef = useRef({
    active: null,
    history: [],
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_RECENT),
      pickColor(colors, 2, DEFAULT_OLD),
      pickColor(colors, 3, DEFAULT_AXIS),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const tick = (ts) => {
      const [bg, recentCol, oldCol, axisCol] = colorsRef.current;
      const state = stateRef.current;

      // Lazily start the very first path on the first tick after mount.
      if (state.active === null) {
        state.active = { points: generatePath(), startTs: ts };
      }

      // If the active path has finished its journey across the canvas,
      // promote it to the head of history and start the next one. We
      // do this BEFORE drawing so the just-completed path appears as
      // history[0] (its first frame as a "background" path) without a
      // visible flicker.
      const elapsed = ts - state.active.startTs;
      if (elapsed >= PATH_DURATION_MS) {
        state.history.unshift({ points: state.active.points });
        if (state.history.length > MAX_HISTORY) {
          state.history.length = MAX_HISTORY;
        }
        state.active = { points: generatePath(), startTs: ts };
      }

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Reference axis at E[X_t] = 0. Dashed so it reads as a guide,
      // not as another path.
      const cy = height / 2;
      ctx.strokeStyle = rgba(axisCol, 0.30);
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 7]);
      ctx.beginPath();
      ctx.moveTo(0, cy);
      ctx.lineTo(width, cy);
      ctx.stroke();
      ctx.setLineDash([]);

      // Origin marker — every path starts here.
      ctx.fillStyle = rgba(axisCol, 0.55);
      ctx.beginPath();
      ctx.arc(0, cy, 3.5, 0, Math.PI * 2);
      ctx.fill();

      // Normalize endpoint variance to 1 by dividing each step value
      // by √N. With AMPLITUDE_FRAC = 0.30 this puts ±1σ of the
      // endpoint distribution at ~30 % of canvas height.
      const yScale = height * AMPLITUDE_FRAC;
      const sqrtN = Math.sqrt(STEPS_PER_PATH);
      const stepCount = STEPS_PER_PATH - 1;

      // Draw history paths back-to-front so the most recently completed
      // path overlays older ones.
      const history = state.history;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let p = history.length - 1; p >= 0; p -= 1) {
        const path = history[p];
        // ageNorm: 0 = freshest (just completed), 1 = oldest in buffer
        const ageNorm = p / Math.max(1, MAX_HISTORY - 1);
        // Quadratic falloff: alpha drops fast at first, then plateaus
        // so older paths still hint at the realization history.
        const alpha = (1 - ageNorm) * (1 - ageNorm) * 0.65 + 0.05;
        const lineWidth = 0.6 + (1 - ageNorm) * 0.9;

        ctx.strokeStyle = blendRgba(recentCol, oldCol, ageNorm, alpha);
        ctx.lineWidth = lineWidth;

        ctx.beginPath();
        for (let i = 0; i < path.points.length; i += 1) {
          const x = (i / stepCount) * width;
          const y = cy + (path.points[i] / sqrtN) * yScale;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Draw the active path up to the current progress. Bright,
      // slightly thicker than history, with a glowing dot at the head.
      const progress = Math.min(1, (ts - state.active.startTs) / PATH_DURATION_MS);
      const visiblePoints = Math.max(2, Math.floor(progress * STEPS_PER_PATH));
      const points = state.active.points;

      ctx.strokeStyle = rgba(recentCol, 0.95);
      ctx.lineWidth = 1.7;
      ctx.beginPath();
      for (let i = 0; i < visiblePoints; i += 1) {
        const x = (i / stepCount) * width;
        const y = cy + (points[i] / sqrtN) * yScale;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Glowing head marker — sells the "currently being drawn" feel.
      const headIdx = visiblePoints - 1;
      const hx = (headIdx / stepCount) * width;
      const hy = cy + (points[headIdx] / sqrtN) * yScale;
      const r = 3.2;
      const halo = ctx.createRadialGradient(hx, hy, 0, hx, hy, r * 5);
      halo.addColorStop(0, rgba(recentCol, 0.6));
      halo.addColorStop(1, rgba(recentCol, 0));
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(hx, hy, r * 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = rgba(recentCol, 1);
      ctx.beginPath();
      ctx.arc(hx, hy, r, 0, Math.PI * 2);
      ctx.fill();

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
